import React from 'react';
import { ArrowLeft, Play, HelpCircle } from 'lucide-react';
import { useApp } from './AppContext';
import { YandexRTB } from './YandexRTB';
import type { Screen } from '../App';

interface PreparationScreenProps {
  onNavigate: (screen: Screen) => void;
  onBack: () => void;
}

export const PreparationScreen: React.FC<PreparationScreenProps> = ({ onNavigate, onBack }) => {
  const { questions, categories, userStats, startTest, getBackgroundClass } = useApp();

  const startCategoryTest = (category: string) => {
    const categoryQuestions = questions.filter(q => q.category === category);
    const shuffled = [...categoryQuestions].sort(() => Math.random() - 0.5);
    const testQuestions = shuffled.slice(0, Math.min(10, shuffled.length));
    startTest('preparation', testQuestions, category);
    onNavigate('test');
  };

  const getCategoryProgress = (category: string) => {
    const categoryQuestions = questions.filter(q => q.category === category);
    const stats = userStats.categoryStats[category];

    // Use the new answeredQuestions array for more accurate progress tracking
    const answeredQuestions = stats?.answeredQuestions || [];
    const answered = answeredQuestions.length;

    // Ensure answered count doesn't exceed total questions
    const validAnswered = Math.min(answered, categoryQuestions.length);

    return {
      answered: validAnswered,
      total: categoryQuestions.length,
      percentage: categoryQuestions.length > 0 ? Math.min((validAnswered / categoryQuestions.length) * 100, 100) : 0
    };
  };

  return (
    <div className={`screen-min-h-full ${getBackgroundClass()} max-w-full lg:max-w-4xl xl:max-w-6xl 2xl:max-w-7xl mx-auto flex flex-col`}>
      {/* Fixed Header */}
      <div className="app-header-fixed flex items-center">
        <button
          onClick={onBack}
          className="mr-2 sm:mr-3 arrow-button flex-shrink-0"
          aria-label="Назад"
        >
          <ArrowLeft className="arrow-icon" />
        </button>

        <div className="flex items-center gap-2 sm:gap-3 flex-1 min-w-0">
          <h1 className="text-lg sm:text-xl md:text-2xl font-semibold glass-text truncate flex items-center gap-2">
            <Play className="h-5 w-5 sm:h-6 sm:w-6 md:h-7 md:w-7 glass-icon" />
            Подготовка
          </h1>
        </div>

        <button
          onClick={() => onNavigate('preparation-help')}
          className="ml-2 sm:ml-3 arrow-button flex-shrink-0"
          aria-label="Помощь"
        >
          <HelpCircle className="arrow-icon" />
        </button>
      </div>

      {/* Scrollable Content */}
      <div className="app-content-scrollable">
        <div className="space-y-4">
          {/* Адаптивная реклама до списка: пользователь видит слот без прокрутки. */}
          <div className="w-full my-4 flex justify-center rounded-xl bg-white/5 min-h-[80px]">
            <YandexRTB type="adaptive" idMobile="R-A-17579918-10" idDesktop="R-A-17579918-11" />
          </div>

          {/* Category Cards */}
          {categories.map((category, index) => {
            const progress = getCategoryProgress(category);
            return (
              <React.Fragment key={category}>
                <button
                  onClick={() => startCategoryTest(category)}
                  className="w-full glass-panel rounded-xl active:bg-white/20 focus-visible:bg-white/15 cursor-pointer"
                >
                  <div className="p-4 md:p-6 text-left">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-medium glass-text text-base md:text-lg">{category}</h3>
                      <Play className="h-5 w-5 md:h-6 md:w-6 glass-icon opacity-60" />
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between text-sm glass-text-secondary">
                        <span>Прогресс: {progress.answered} из {progress.total} вопросов</span>
                        <span>{Math.round(progress.percentage)}%</span>
                      </div>
                      <div className="w-full glass-progress-bg rounded-full h-2">
                        <div
                          className="glass-progress-fill h-2 rounded-full"
                          style={{ width: `${progress.percentage}%` }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </button>
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
};